import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Button, Divider, Form, Icon, Modal } from "semantic-ui-react";
import * as moment from "moment";

import { Input } from "@neworbit/simpleui-input";

import { EditTaskCompletionDataModel, TaskModel } from "@teamprevent/common/task/taskActions/models";
import { DropdownOption } from "@teamprevent/common/questionnaires/model";
import { useTimeHasAlreadyPassedValidator } from "@teamprevent/common/validators/timeHasAlreadyPassed";
import { useTimeIsAfterValidator } from "@teamprevent/common/validators/timeIsAfter";

import { createValidator } from "not-valid";

import { ValidationResultType } from "not-valid/bin/results";

import { ApplicationState } from "../../applicationState";
import { editTaskCompletionData } from "../../crud/ohCase/actions";

interface Props {
    task: TaskModel;
    adminUserOptions: DropdownOption[];
}
const dateFirstOfferedYearInRange = createValidator(
    (value: moment.Moment) => value.isBetween(moment().subtract(2, "years"), moment().add(2, "years"), "year", "[]"),
    "Please enter a valid date");

const jan1st2000 = moment("2000-01-01");

export const EditTaskCompletionDataButton: React.FunctionComponent<Props> = (props: Props) => {
    const dispatch = useDispatch();

    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [actualCompletionDate, setActualCompletionDate] = React.useState<moment.Moment | undefined>(props.task.actualCompletionDate);
    const timeHasAlreadyPassedValidator = useTimeHasAlreadyPassedValidator();
    const isAfterJan1st2000Validator = useTimeIsAfterValidator(jan1st2000);

    const [actuallyCarriedOutById, setActuallyCarriedOutById] = React.useState<string | undefined>(props.task.actuallyCarriedOutBy?.id);
    const [earlierAppointmentDate, setEarlierAppointmentDate] = React.useState<moment.Moment | undefined>(props.task.earlierAppointmentDate);
    const [submitted, setSubmitted] = React.useState<boolean>(false);

    const customerId = useSelector((state: ApplicationState) => state.router.params!.customerId);
    const isActuallyCarriedOutByRequired = !!props.task.completedBy?.id;

    const actualCompletionDateValidators = React.useMemo(
        () => [timeHasAlreadyPassedValidator, isAfterJan1st2000Validator],
        [timeHasAlreadyPassedValidator, isAfterJan1st2000Validator]);

    const onEdit = React.useCallback(
        () => {
            setModalOpen(true);

            const date = props.task.actualCompletionDate ?? props.task.completedDate;

            if (date) {
                setActualCompletionDate(date);
            }

            const completedById = props.task.actuallyCarriedOutBy?.id ?? props.task.completedBy?.id;
            setActuallyCarriedOutById(completedById);
        },
        [props.task]
    );

    const onCancel = React.useCallback(() => {
        setModalOpen(false);
        setSubmitted(false);
        setActualCompletionDate(undefined);
        setActuallyCarriedOutById(undefined);
        setEarlierAppointmentDate(undefined);
    }, []);

    const onConfirm = React.useCallback(() => {
        setSubmitted(true);
        if (!actualCompletionDate || (isActuallyCarriedOutByRequired && !actuallyCarriedOutById)) {
            return;
        }

        if (timeHasAlreadyPassedValidator(actualCompletionDate).type === ValidationResultType.Fail) {
            return;
        }

        if (isAfterJan1st2000Validator(actualCompletionDate).type === ValidationResultType.Fail) {
            return;
        }

        if (earlierAppointmentDate) {
            const earlierAppointmentDateValid = dateFirstOfferedYearInRange(earlierAppointmentDate);
            if (earlierAppointmentDateValid.type === ValidationResultType.Fail) {
                return;
            }
        }
        const model: EditTaskCompletionDataModel = {
            taskId: props.task.id,
            actualCompletionDate,
            actuallyCarriedOutById: actuallyCarriedOutById ?? "",
            earlierAppointmentDate
        };
        dispatch(editTaskCompletionData(customerId, props.task.caseId, model));
        setModalOpen(false);
    }, [actualCompletionDate,
        actuallyCarriedOutById,
        customerId,
        dispatch,
        earlierAppointmentDate,
        isActuallyCarriedOutByRequired,
        props.task.caseId,
        props.task.id,
        timeHasAlreadyPassedValidator,
        isAfterJan1st2000Validator]);

    const onActualCompletionDateChange = React.useCallback((completionDate: moment.Moment) => setActualCompletionDate(completionDate), []);

    const onActuallyCarriedOutByChange = React.useCallback((adminUser: string) => setActuallyCarriedOutById(adminUser), []);

    const onEarlierAppointmentDateChange = React.useCallback((date: moment.Moment) => setEarlierAppointmentDate(date), []);

    return (
        <>
            <Modal open={modalOpen}>
                <Modal.Header content="Edit task" />
                <Modal.Content>
                    <Form>
                        <Input.Date
                            required
                            value={actualCompletionDate}
                            validation={actualCompletionDateValidators}
                            showErrors={submitted}
                            label="Actual Completion Date"
                            onChange={onActualCompletionDateChange}
                        />
                        <Divider hidden />
                        <Input.Dropdown
                            required={isActuallyCarriedOutByRequired}
                            value={actuallyCarriedOutById}
                            showErrors={submitted}
                            label="Actually Carried Out By"
                            options={props.adminUserOptions}
                            onChange={onActuallyCarriedOutByChange}
                            search
                        />
                        {props.task.isBookTask &&
                        <>
                            <Divider hidden />
                            <Input.Date
                                value={earlierAppointmentDate}
                                showErrors={submitted}
                                label="Date First Offered"
                                onChange={onEarlierAppointmentDateChange}
                                validation={[dateFirstOfferedYearInRange]}
                            />
                        </>}
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={onCancel} />
                    <Button content="Submit" onClick={onConfirm} />
                </Modal.Actions>
            </Modal>
            <Button basic icon={<Icon name="ellipsis horizontal" color="orange" />} size="mini" floated="right" onClick={onEdit} />
        </>
    );
};
